import {
  Grid,
  Typography,
  Box,
  Paper,
  FormControl,
  FormLabel,
  RadioGroup,
  FormControlLabel,
  Radio,
  Button,
  Checkbox,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  Chip,
} from "@mui/material";
import { useState, useMemo, useCallback } from "react";
import { useBlockedDates } from "../../../../hooks/useBlockedDates";
import { AppDatePicker } from "../../../../components/datepickers";
import {
  convertToTimezone,
  DATE_FORMATS,
  reactDatePickerFilterPassedTime,
  formatDate,
} from "../../../../services/date-and-time.utils";
import { AppModalDialogBox } from "../../../../components/modals";
import {
  CalendarToday,
  AccessTime,
  Add,
  Delete,
} from "@mui/icons-material";
import { SaveAndCancelButton } from "../../../../components/buttons";

interface Slot {
  id: string;
  from: Date;
  to: Date;
  ambassadorTimezoneFrom: string;
  ambassadorTimezoneTo: string;
  vendorTimezoneFrom: string;
  vendorTimezoneTo: string;
  dateKey: string;
}

function RescheduleSlotsModal({
  handleModalClose,
  invitationData,
  onSubmit,
}: any) {
  const [selectedType, setSelectedType] = useState<"vendor" | "ambassador">(
    "vendor"
  );
  const [selectedDate, setSelectedDate] = useState<Date | null>(null);
  const [slots, setSlots] = useState<Slot[]>([]);
  const [selfTimeSlotsSelection, setSelfTimeSlotsSelection] = useState(false);
  const { blockedDates, onMonthChange } = useBlockedDates();

  const handleTypeChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    setSelectedType(event.target.value as "vendor" | "ambassador");
  };

  const handleSelfTimeSlotsChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    setSelfTimeSlotsSelection(event.target.checked);
  };

  const handleDateChange = useCallback((date: Date | null) => {
    setSelectedDate(date);
  }, []);

  const handleAddSlot = useCallback(() => {
    if (!selectedDate) return;

    const to = new Date(selectedDate.getTime() + 45 * 60000);
    const dateKey = formatDate(selectedDate, DATE_FORMATS.yyymmdd);

    const newSlot: Slot = {
      id: `${Date.now()}-${Math.random()}`,
      from: new Date(selectedDate),
      to: to,
      ambassadorTimezoneFrom: convertToTimezone(
        selectedDate,
        invitationData?.ambassador?.timezone?.value,
        DATE_FORMATS.fullDateWithTime
      ),
      ambassadorTimezoneTo: convertToTimezone(
        to,
        invitationData?.ambassador?.timezone?.value,
        DATE_FORMATS.hourMinWithAMPM
      ),
      vendorTimezoneFrom: convertToTimezone(
        selectedDate,
        invitationData?.client?.timezone?.value,
        DATE_FORMATS.fullDateWithTime
      ),
      vendorTimezoneTo: convertToTimezone(
        to,
        invitationData?.client?.timezone?.value,
        DATE_FORMATS.hourMinWithAMPM
      ),
      dateKey: dateKey,
    };

    setSlots((prevSlots) => [...prevSlots, newSlot]);
    setSelectedDate(null);
  }, [selectedDate, invitationData]);

  const handleRemoveSlot = useCallback((slotId: string) => {
    setSlots((prevSlots) => prevSlots.filter((slot) => slot.id !== slotId));
  }, []);

  const { groupedSlots, sortedDates } = useMemo(() => {
    const grouped: { [key: string]: Slot[] } = {};
    slots.forEach((slot) => {
      if (!grouped[slot.dateKey]) {
        grouped[slot.dateKey] = [];
      }
      grouped[slot.dateKey].push(slot);
    });
    const sorted = Object.keys(grouped).sort();
    return { groupedSlots: grouped, sortedDates: sorted };
  }, [slots]);

  const handleSubmit = useCallback(() => {
    if (!selfTimeSlotsSelection && slots.length === 0) return;

    const payload = {
      slots: slots.map((slot) => ({
        from: slot.from,
        to: slot.to,
      })),
      selectedType: selectedType,
      selfTimeSlotsSelection,
    };

    onSubmit(payload);
  }, [slots, selectedType, selfTimeSlotsSelection, onSubmit]);

  const isSubmitDisabled = !selfTimeSlotsSelection && slots.length === 0;

  return (
    <AppModalDialogBox
      isModalOpen={true}
      title="Reschedule"
      handleClose={handleModalClose}
      size={"md"}
      actionButtonRenderer={
        <SaveAndCancelButton
          primaryText={"Reschedule"}
          defaultText={"Cancel"}
          onPrimaryActionClick={handleSubmit}
          onDefaultButtonClick={handleModalClose}
          isPrimaryButtonDisabled={isSubmitDisabled}
        />
      }
    >
      <Box sx={{ p: 1 }}>
        {/* Select Type Section */}
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            border: "1px solid #e0e0e0",
            backgroundColor: "#fafafa",
          }}
        >
          <FormControl component="fieldset">
            <FormLabel
              component="legend"
              sx={{
                fontWeight: 600,
                fontSize: 16,
                color: "text.primary",
                mb: 2,
              }}
            >
              Select for whom you want to schedule the time
            </FormLabel>
            <RadioGroup
              row
              aria-labelledby="slot-type-radio-buttons-group-label"
              name="slot-type-radio-buttons-group"
              value={selectedType}
              onChange={handleTypeChange}
            >
              <FormControlLabel
                value="vendor"
                control={<Radio />}
                label="Vendor"
              />
              <FormControlLabel
                value="ambassador"
                control={<Radio />}
                label="Ambassador"
              />
            </RadioGroup>
          </FormControl>
        </Paper>

        <FormControlLabel
          control={
            <Checkbox
              checked={selfTimeSlotsSelection}
              onChange={handleSelfTimeSlotsChange}
              color="primary"
            />
          }
          label="Self-Selection of Time Slots"
          sx={{ mb: 2, ml: 0.5 }}
        />

        <Box
          sx={{
            visibility: selfTimeSlotsSelection ? "hidden" : "visible",
            pointerEvents: selfTimeSlotsSelection ? "none" : "auto",
          }}
        >
        <Paper
          elevation={0}
          sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            border: "1px solid #e0e0e0",
            backgroundColor: "#fafafa",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", mb: 2 }}>
            <CalendarToday sx={{ mr: 1.5, color: "primary.main" }} />
            <Typography
              variant="h6"
              sx={{
                fontWeight: 600,
                color: "text.primary",
                fontSize: 16,
              }}
            >
              Select Date & Time
            </Typography>
          </Box>

          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={4}>
              <AppDatePicker
                dateFormat={"Pp"}
                showTimeSelect
                selectedDate={selectedDate}
                setDate={handleDateChange}
                minDate={new Date()}
                filterTime={reactDatePickerFilterPassedTime}
                blockedDates={blockedDates}
                onMonthChange={onMonthChange}
              />
            </Grid>
            <Grid item xs={12} sm={4}>
              <Button
                variant="contained"
                startIcon={<Add />}
                onClick={handleAddSlot}
                disabled={!selectedDate}
                disableRipple
                disableElevation
                sx={{
                  height: 40,
                  transition: "none",
                  "&:hover": {
                    backgroundColor: "primary.main",
                    transition: "none",
                  },
                  "&:active": {
                    backgroundColor: "primary.main",
                    transition: "none",
                  },
                  "&:focus": {
                    backgroundColor: "primary.main",
                    transition: "none",
                  },
                }}
              >
                Add Time
              </Button>
            </Grid>
          </Grid>
        </Paper>

        {slots.length > 0 && (
          <Box>
            <Typography
              variant="h6"
              sx={{
                mb: 2,
                fontWeight: 600,
                color: "text.primary",
                display: "flex",
                alignItems: "center",
                fontSize: 16,
              }}
            >
              <AccessTime sx={{ mr: 1.5, color: "primary.main" }} />
              Selected Times
            </Typography>

            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                border: "1px solid #e0e0e0",
                borderRadius: 2,
                maxHeight: 400,
                overflow: "auto",
              }}
            >
              <Table stickyHeader>
                <TableHead>
                  <TableRow>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        backgroundColor: "#f5f5f5",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      Date
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        backgroundColor: "#f5f5f5",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      Time
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        backgroundColor: "#f5f5f5",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      Ambassador Timezone
                      <Typography variant="caption" display="block" fontSize={11} color="text.secondary" fontWeight={400}>
                        {invitationData?.ambassador?.timezone?.text || invitationData?.ambassador?.timezone?.value}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        backgroundColor: "#f5f5f5",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                      }}
                    >
                      Vendor Timezone
                      <Typography variant="caption" display="block" fontSize={11} color="text.secondary" fontWeight={400}>
                        {invitationData?.client?.timezone?.text || invitationData?.client?.timezone?.value}
                      </Typography>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontWeight: 600,
                        backgroundColor: "#f5f5f5",
                        position: "sticky",
                        top: 0,
                        zIndex: 1,
                        width: 80,
                      }}
                    >
                      Action
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedDates.map((dateKey) => {
                    const dateSlots = groupedSlots[dateKey];
                    const displayDate = formatDate(
                      dateKey,
                      DATE_FORMATS.fullDate
                    );

                    return dateSlots.map((slot, index) => (
                      <TableRow key={slot.id}>
                        {index === 0 && (
                          <TableCell
                            rowSpan={dateSlots.length}
                            sx={{
                              fontWeight: 600,
                              verticalAlign: "top",
                              borderRight: "1px solid #e0e0e0",
                            }}
                          >
                            <Chip
                              label={displayDate}
                              size="small"
                              color="primary"
                              variant="outlined"
                              sx={{ fontWeight: 600 }}
                            />
                          </TableCell>
                        )}
                        <TableCell>
                          <Typography variant="body2" fontSize={13} fontWeight={500}>
                            {formatDate(slot.from, DATE_FORMATS.hourMinWithAMPM)} -{" "}
                            {formatDate(slot.to, DATE_FORMATS.hourMinWithAMPM)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontSize={13}>
                            {slot.ambassadorTimezoneFrom} -{" "}
                            {slot.ambassadorTimezoneTo}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" fontSize={13}>
                            {slot.vendorTimezoneFrom} - {slot.vendorTimezoneTo}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleRemoveSlot(slot.id)}
                            disableRipple
                            sx={{
                              p: 0.5,
                              transition: "none",
                              "&:hover": {
                                backgroundColor: "transparent",
                                transition: "none",
                              },
                              "&:active": {
                                backgroundColor: "transparent",
                                transition: "none",
                              },
                              "&:focus": {
                                backgroundColor: "transparent",
                                transition: "none",
                              },
                            }}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </TableCell>
                      </TableRow>
                    ));
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
        </Box>
      </Box>
    </AppModalDialogBox>
  );
}

export { RescheduleSlotsModal };

